import groupBy from 'lodash-es/groupBy';
import { format } from 'date-fns';

import { SummaryCardOption } from '@design-system/components/organisms/summary-card/entities/summary-card.types';
import { Fares, PriceType } from '@activities/activities-results/entities/activities-results';
import { PaxType } from '@booking/entities/pax-type';
import { ActivitiesValidateResponse, ActivityFareBookParamsVM } from '@activities/entities/activities-booking';
import { toDateUtc } from '@ux/mappers/date.mapper';
import {
  DateCalendarLocale, DateCalendarLocaleValue
} from '@design-system/components/organisms/date-calendar/entities/date-calendar.types';
import { environment as ENV } from '@environment';
import { getActivityTypeLabelKey } from '@activities/mappers/activities-type.mapper';

const locale = DateCalendarLocaleValue?.[ENV.language?.toLowerCase() as DateCalendarLocale] ?? null;

export function getGroupAttributes(fares: Fares[]): {[key: string]: unknown} {
  const fareAttributes = fares?.map(fare => fare.Attributes) ?? [];
  const attributes = [].concat(...fareAttributes);
  return groupBy(attributes, 'Code');
}

export function getFareAttributes(
  validation: ActivitiesValidateResponse, faresVm: ActivityFareBookParamsVM[]
): SummaryCardOption[] {
  const isPerService = validation?.Products?.ProductsDetails?.[0]?.Fares?.[0]?.PriceType === PriceType.PerService;
  const fares = validation?.Products?.ProductsDetails?.[0]?.Fares;
  const activityType = validation?.Products?.ProductsDetails?.[0]?.ActivityType;
  const productName = validation?.Products?.ProductsDetails?.[0]?.ProductName;
  const group = getGroupAttributes(fares);
  return [
    productName && {
      Title: 'TOUR_AND_ACTIVITIES_MODULE.RESUME.OPTION',
      Description: productName
    },
    activityType && {
      Title: 'TOUR_AND_ACTIVITIES_MODULE.RESUME.TYPE',
      Description: `${getActivityTypeLabelKey(activityType)}`
    },
    {
      Title: isPerService ? 'TOUR_AND_ACTIVITIES_MODULE.RESUME.QUANTITY' : 'TOUR_AND_ACTIVITIES_MODULE.RESUME.PERSONS',
      Description: `${getQuantity(fares, isPerService)}`
    },
    faresVm?.[0]?.SelectedDate && {
      Title: 'TOUR_AND_ACTIVITIES_MODULE.RESUME.DATE',
      Description: getDateString(faresVm?.[0]?.SelectedDate),
    },
    group?.Times && {
      Title: 'TOUR_AND_ACTIVITIES_MODULE.RESUME.HOURS',
      Description: group?.Times?.[0]?.Value
    }
  ].filter(item => !!item);
}

export function getDateString(selectedDate: string): string {
  return toTitleCase(format(toDateUtc(selectedDate), 'EEEE, dd LLL yyyy', { locale: locale }));
}

function getQuantity(fares: Fares[], isPerService: boolean): number {
  if (isPerService) {
    return fares?.reduce(reduceQuantity, 0) ?? 0;
  } else {
    const group = fares ? groupBy(fares, 'PaxType') : {};
    const adults = group?.[PaxType.ADULTS]?.reduce(reduceQuantity, 0) ?? 0;
    const children = group?.[PaxType.CHILDREN]?.reduce(reduceQuantity, 0) ?? 0;
    const infants = group?.[PaxType.INFANTS]?.reduce(reduceQuantity, 0) ?? 0;
    return adults + children + infants;
  }
}

function toTitleCase(value: string): string {
  let str;
  str = value ? value?.toLowerCase().split(' ') : [];
  for (var i = 0; i < str.length; i++) {
    str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
  }
  return str.join(' ');
}

function reduceQuantity(accumulator: number, currentValue: Fares): number {
  return accumulator + (currentValue?.Quantity ?? 0);
}
